import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Search, X, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import api, { absoluteAsset } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import ImageUpload from '../../components/ImageUpload';
import ExcelImport from '../../components/ExcelImport';
import Barcode from '../../components/Barcode';
import { useToast } from '../../hooks/use-toast';
import { sortCategories, groupByBrand } from '../../services/sort';

const emptyForm = { id: '', name: '', brandId: '', brand: '', category: '', code: '', weight: '', unitsPerBox: 0, barcode: '', image: '', hidden: false, cosmeticAreaId: '', cosmeticTypeId: '', cosmeticLine: '', cosmeticShade: '' };

const isCosmetics = (cat) => (cat || '').trim().toLowerCase().replace('é', 'e') === 'cosmeticos';

const ProductsAdmin = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cosmeticAreas, setCosmeticAreas] = useState([]);
  const [cosmeticTypes, setCosmeticTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b, c, ca, ct] = await Promise.all([
        api.get('/products?include_hidden=true'),
        api.get('/brands'),
        api.get('/categories'),
        api.get('/cosmetic-areas'),
        api.get('/cosmetic-types'),
      ]);
      setProducts(p.data);
      setBrands(b.data);
      setCategories(sortCategories(c.data));
      setCosmeticAreas(ca.data);
      setCosmeticTypes(ct.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const brandByName = useMemo(() => {
    const map = {};
    brands.forEach(b => { map[b.name] = b; });
    return map;
  }, [brands]);

  const openNew = () => {
    const first = brands[0];
    setForm({ ...emptyForm, brandId: first?.id || '', brand: first?.name || '', category: first?.category || '' });
    setOpen(true);
  };
  const openEdit = (p) => { setForm({ ...p }); setOpen(true); };

  const onBrandChange = (brandId) => {
    const br = brands.find(b => b.id === brandId);
    if (!br) return;
    setForm(f => {
      const next = { ...f, brandId, brand: br.name, category: br.category };
      if (!isCosmetics(br.category)) {
        next.cosmeticAreaId = '';
        next.cosmeticTypeId = '';
        next.cosmeticLine = '';
        next.cosmeticShade = '';
      }
      return next;
    });
  };

  const onAreaChange = (areaId) => {
    setForm(f => ({ ...f, cosmeticAreaId: areaId, cosmeticTypeId: '' }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.brandId || !form.code.trim()) {
      toast({ title: 'Completa nombre, marca y código' });
      return;
    }
    setSaving(true);
    try {
      const cosmetics = isCosmetics(form.category);
      const payload = {
        name: form.name.trim(),
        brandId: form.brandId,
        brand: form.brand,
        category: form.category,
        code: form.code.trim(),
        weight: form.weight || '',
        unitsPerBox: parseInt(form.unitsPerBox, 10) || 0,
        barcode: form.barcode || '',
        image: form.image || '',
        hidden: !!form.hidden,
        cosmeticAreaId: cosmetics ? (form.cosmeticAreaId || null) : null,
        cosmeticTypeId: cosmetics ? (form.cosmeticTypeId || null) : null,
        cosmeticLine: cosmetics ? (form.cosmeticLine || '') : '',
        cosmeticShade: cosmetics ? (form.cosmeticShade || '') : '',
      };
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post('/products', payload);
      toast({ title: form.id ? 'Producto actualizado' : 'Producto creado' });
      setOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/products/${deleting.id}`);
      toast({ title: 'Producto eliminado' });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo eliminar' });
    }
  };

  const toggleVisibility = async (product) => {
    try {
      const { data } = await api.patch(`/products/${product.id}/visibility`);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, hidden: data.hidden } : p));
      toast({
        title: data.hidden ? 'Producto oculto' : 'Producto visible',
        description: data.hidden
          ? 'Ya no aparece en el catálogo público.'
          : 'Ahora aparece en el catálogo público.',
      });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo cambiar la visibilidad' });
    }
  };

  // Build category > brand grouped structure
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    return categories.map(cat => {
      const items = products
        .filter(p => p.category === cat.name)
        .filter(p =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          (p.brand || '').toLowerCase().includes(q) ||
          (p.code || '').toLowerCase().includes(q) ||
          (p.barcode || '').toLowerCase().includes(q)
        );
      return { ...cat, items, brandGroups: groupByBrand(items) };
    });
  }, [products, categories, query]);

  const totalShown = grouped.reduce((s, c) => s + c.items.length, 0);
  const toggleCategory = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Productos</h2>
          <p className="text-sm text-slate-500">
            {products.length} en total{query && ` · ${totalShown} encontrados`}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..." className="h-10 w-full border-slate-200 bg-white pl-9 sm:w-64" />
          </div>
          <Button onClick={openNew} disabled={brands.length === 0} className="h-10 gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
            <Plus className="h-4 w-4" /> Nuevo producto
          </Button>
        </div>
      </div>

      <ExcelImport entity="products" onImported={load} />

      {brands.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Primero debes crear al menos una marca antes de agregar productos.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
      ) : totalShown === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center text-slate-500">
          {query ? `No se encontraron productos para "${query}".` : 'Aún no hay productos. Crea uno o importa desde Excel.'}
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(cat => {
            if (cat.items.length === 0) return null;
            const isCollapsed = !!collapsed[cat.id];
            return (
              <section key={cat.id} className="overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-emerald-50 to-white px-5 py-3.5 transition-colors hover:bg-emerald-50"
                >
                  <div className="flex items-center gap-3">
                    {isCollapsed
                      ? <ChevronRight className="h-5 w-5 text-emerald-700" />
                      : <ChevronDown className="h-5 w-5 text-emerald-700" />
                    }
                    <div className="h-2 w-10 rounded-full bg-emerald-600" />
                    <h3 className="text-lg font-bold text-slate-900">{cat.name}</h3>
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                      {cat.items.length}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500">{cat.brandGroups.length} {cat.brandGroups.length === 1 ? 'marca' : 'marcas'}</span>
                </button>

                {!isCollapsed && (
                  <div className="divide-y divide-emerald-100">
                    {cat.brandGroups.map(group => {
                      const brandInfo = brandByName[group.brand];
                      return (
                        <div key={`${cat.id}-${group.brand}`} className="p-4 sm:p-5">
                          <div className="mb-3 flex items-center gap-3">
                            {brandInfo?.logo ? (
                              <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white p-1">
                                <img src={absoluteAsset(brandInfo.logo)} alt={group.brand} className="h-full w-full object-contain" />
                              </div>
                            ) : (
                              <div className="h-6 w-1 rounded-full bg-emerald-400" />
                            )}
                            <h4 className="text-sm font-semibold text-slate-800">{group.brand}</h4>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                              {group.items.length}
                            </span>
                            <div className="h-px flex-1 bg-emerald-100" />
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-slate-200">
                            <table className="min-w-full divide-y divide-slate-100">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Imagen</th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Producto</th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Código</th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">C. Barras</th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Peso</th>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Uds/Caja</th>
                                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-500">Acciones</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 bg-white">
                                {group.items.map(p => (
                                  <tr key={p.id} className={`hover:bg-slate-50 ${p.hidden ? 'bg-slate-50/50 opacity-60' : ''}`}>
                                    <td className="px-3 py-2">
                                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-slate-100 bg-slate-50 p-1">
                                        {p.image ? <img src={absoluteAsset(p.image)} alt={p.name} className="h-full w-full object-contain" /> : <span className="text-xs text-slate-400">-</span>}
                                      </div>
                                    </td>
                                    <td className="max-w-xs px-3 py-2">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-slate-900">{p.name}</p>
                                        {p.hidden && (
                                          <Badge variant="secondary" className="shrink-0 bg-amber-100 text-amber-800 hover:bg-amber-100">
                                            <EyeOff className="mr-1 h-3 w-3" /> Oculto
                                          </Badge>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{p.code}</td>
                                    <td className="px-3 py-2">
                                      {p.barcode ? <Barcode value={p.barcode} height={28} fontSize={8} /> : <span className="text-xs text-slate-400">-</span>}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-slate-700">{p.weight || '-'}</td>
                                    <td className="px-3 py-2 text-sm text-slate-700">{p.unitsPerBox || '-'}</td>
                                    <td className="px-3 py-2 text-right">
                                      <div className="flex justify-end gap-1.5">
                                        <Button
                                          size="sm" variant="outline"
                                          onClick={() => toggleVisibility(p)}
                                          title={p.hidden ? 'Mostrar en catálogo' : 'Ocultar del catálogo'}
                                          className={`h-7 gap-1 px-2 text-xs ${
                                            p.hidden
                                              ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                                              : 'border-slate-300'
                                          }`}
                                        >
                                          {p.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="h-7 gap-1 border-slate-300 px-2 text-xs">
                                          <Pencil className="h-3 w-3" /> Editar
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setDeleting(p)} className="h-7 gap-1 border-red-200 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700">
                                          <Trash2 className="h-3 w-3" /> Eliminar
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre del producto</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" required />
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={form.brandId} onValueChange={onBrandChange}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Selecciona marca" /></SelectTrigger>
                  <SelectContent>
                    {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name} <span className="ml-1 text-xs text-slate-400">({b.category})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoría (automática)</Label>
                <Input value={form.category} disabled className="h-11 bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Código</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-11" placeholder="Ej. PR-00459" required />
              </div>
              <div className="space-y-2">
                <Label>Peso / Volumen</Label>
                <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="h-11" placeholder="Ej. 500 ml, 2 Kg" />
              </div>
              <div className="space-y-2">
                <Label>Unidades por caja</Label>
                <Input type="number" min="0" value={form.unitsPerBox} onChange={(e) => setForm({ ...form, unitsPerBox: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Código de barras</Label>
                <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="h-11" placeholder="EAN-13 (13 díg.) / UPC-A (12 díg.) / opcional" />
                {form.barcode && (
                  <div className="rounded-md border border-slate-100 bg-white p-2">
                    <Barcode value={form.barcode} height={40} fontSize={10} />
                  </div>
                )}
              </div>
            </div>
            {isCosmetics(form.category) && (
              <div className="rounded-lg border border-pink-200 bg-pink-50/60 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-pink-800">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-pink-100 text-pink-700">💄</span>
                  Datos de cosmético
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Área</Label>
                    <Select value={form.cosmeticAreaId || ''} onValueChange={onAreaChange}>
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Selecciona área" /></SelectTrigger>
                      <SelectContent>
                        {cosmeticAreas.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select
                      value={form.cosmeticTypeId || ''}
                      onValueChange={(v) => setForm({ ...form, cosmeticTypeId: v })}
                      disabled={!form.cosmeticAreaId}
                    >
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder={form.cosmeticAreaId ? 'Selecciona tipo' : 'Elige un área primero'} /></SelectTrigger>
                      <SelectContent>
                        {cosmeticTypes.filter(t => t.areaId === form.cosmeticAreaId).map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Línea</Label>
                    <Input value={form.cosmeticLine} onChange={(e) => setForm({ ...form, cosmeticLine: e.target.value })} className="h-11 bg-white" placeholder="Ej. Volume 500, Mega Last" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tono</Label>
                    <Input value={form.cosmeticShade} onChange={(e) => setForm({ ...form, cosmeticShade: e.target.value })} className="h-11 bg-white" placeholder="Ej. 01 Negro, 250 Rose" />
                  </div>
                </div>
              </div>
            )}
            <ImageUpload label="Imagen del producto" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />

            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-3">
                {form.hidden
                  ? <EyeOff className="h-5 w-5 text-amber-600" />
                  : <Eye className="h-5 w-5 text-emerald-700" />
                }
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {form.hidden ? 'Producto oculto' : 'Producto visible'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {form.hidden
                      ? 'No aparece en el catálogo público (útil cuando está agotado).'
                      : 'Visible para los clientes en el catálogo público.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={!form.hidden}
                onCheckedChange={(v) => setForm({ ...form, hidden: !v })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="gap-1"><X className="h-4 w-4" /> Cancelar</Button>
              <Button type="submit" disabled={saving} className="gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleting?.name}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsAdmin;
